"use client";

import { useEffect } from "react";
import {
  getAuthRememberMe,
  getToken,
  getUser,
  setToken,
  setUser,
} from "@/app/services/utils";

const REQUEST_TYPE = "MTP_SESSION_REQUEST";
const HANDOFF_TYPE = "MTP_SESSION_HANDOFF";
const BROADCAST_CHANNEL = "mtp-session";
export const SESSION_HYDRATED_EVENT = "mtp-session-hydrated";

type SessionHandoffPayload = {
  type: typeof HANDOFF_TYPE;
  token: string;
  user?: unknown;
  rememberMe?: boolean;
};

function applyHandoff(data: SessionHandoffPayload): boolean {
  if (!data.token || getToken()) return false;

  const roleId =
    (data.user as { role?: { id?: number }; role_id?: number } | undefined)
      ?.role?.id ??
    (data.user as { role_id?: number } | undefined)?.role_id;

  setToken(data.token, Boolean(data.rememberMe), roleId ?? null);
  if (data.user) {
    setUser(data.user, Boolean(data.rememberMe), roleId ?? null);
  }
  window.dispatchEvent(new Event(SESSION_HYDRATED_EVENT));
  return true;
}

function publishSession(channel: BroadcastChannel | null): void {
  const token = getToken();
  if (!token || !channel) return;
  try {
    channel.postMessage({
      type: HANDOFF_TYPE,
      token,
      user: getUser(),
      rememberMe: getAuthRememberMe(),
    } satisfies SessionHandoffPayload);
  } catch {
    /* BroadcastChannel may be unavailable */
  }
}

/**
 * Syncs auth into sibling tabs via BroadcastChannel, and best-effort into
 * target=_blank tabs via window.opener postMessage.
 * Complements HttpOnly mtp_session cookie auth so getToken() works after hydrate.
 * Same-origin _blank links keep rel="noopener" — cookie hydrate is the durable path.
 */
export function CrossTabSessionSync() {
  useEffect(() => {
    const origin = window.location.origin;
    let channel: BroadcastChannel | null = null;

    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL);
    } catch {
      channel = null;
    }

    const onBroadcast = (event: MessageEvent) => {
      if (event.data?.type === REQUEST_TYPE) {
        publishSession(channel);
        return;
      }
      if (event.data?.type === HANDOFF_TYPE) {
        applyHandoff(event.data as SessionHandoffPayload);
      }
    };

    const onWindowMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;

      if (event.data?.type === REQUEST_TYPE) {
        const token = getToken();
        const user = getUser();
        if (!token || !event.source || !("postMessage" in event.source)) {
          return;
        }
        (event.source as Window).postMessage(
          {
            type: HANDOFF_TYPE,
            token,
            user,
            rememberMe: getAuthRememberMe(),
          } satisfies SessionHandoffPayload,
          origin
        );
        return;
      }

      if (event.data?.type === HANDOFF_TYPE) {
        applyHandoff(event.data as SessionHandoffPayload);
      }
    };

    channel?.addEventListener("message", onBroadcast);
    window.addEventListener("message", onWindowMessage);

    if (!getToken()) {
      try {
        channel?.postMessage({ type: REQUEST_TYPE });
      } catch {
        /* ignore */
      }
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage({ type: REQUEST_TYPE }, origin);
        } catch {
          /* opener may be inaccessible (noopener) */
        }
      }
    } else {
      // Newly logged-in tab can publish to siblings that opened earlier.
      publishSession(channel);
    }

    return () => {
      window.removeEventListener("message", onWindowMessage);
      channel?.removeEventListener("message", onBroadcast);
      channel?.close();
    };
  }, []);

  return null;
}
